import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, X, MapPin, Store, Palette, Hammer, Calendar as CalendarIcon, Camera, Navigation, Home as HomeIcon, Crosshair, Music, Compass, ArrowUpDown, Share2, TrendingUp, ShieldCheck, Sparkles, Coffee, ShoppingBag, Heart, Wheat, Landmark, Wrench } from "lucide-react";
import { toast } from "sonner";
import MapView from "@/components/MapView";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import PhotoLightbox from "@/components/PhotoLightbox";
import { useAuth } from "@/context/AuthContext";
import useGeolocation, { distanceKm } from "@/hooks/useGeolocation";
import { Slider } from "@/components/ui/slider";
import HomePinDialog from "@/components/HomePinDialog";
import ScoutTripsDialog from "@/components/ScoutTripsDialog";
import ExploreLocationDialog from "@/components/ExploreLocationDialog";
import GigLightbox from "@/components/GigLightbox";
import OnboardingTour from "@/components/OnboardingTour";
import { Bookmark } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import LayersMenu from "@/components/LayersMenu";
import WhereMenu from "@/components/WhereMenu";

// Little kind-tinted placeholder that renders when a listing/gig/photo has no cover image.
// Uses the layer colour so cards feel intentional even when the owner hasn't uploaded yet.
function CardImage({ src, alt, meta, className = "" }) {
    if (src) return <img loading="lazy" decoding="async" src={thumb(src, 600)} alt={alt} className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ${className}`} />;
    const Icon = meta?.Icon || MapPin;
    return (
        <div className={`w-full h-full flex items-center justify-center ${className}`}
             style={{ background: `linear-gradient(135deg, ${meta?.hex || "hsl(30 8% 82%)"}22, ${meta?.hex || "hsl(30 8% 72%)"}44)` }}>
            <Icon size={40} style={{ color: meta?.hex || "hsl(30 8% 40%)" }} strokeWidth={1.5} />
        </div>
    );
}

const GIG_SUB_LABELS = { all: "All", music: "Music", quiz: "Pub quiz", art: "Performance art", performance: "Performance", comedy: "Comedy" };
const GIG_TIME_LABELS = { all: "All", now: "On now", today: "Today", week: "This week" };
const SORT_LABELS = { nearest: "Nearest", newest: "Newest", rated: "Highest rated" };

// localStorage keys for the per-browser layer preferences. Bumped from v1 -> v2
// (2026-09-13) when `business` split into `eat_drink` / `shop` / `services`.
const LAYER_LS_KEY = "fromhere_active_kinds_v2";
const GIG_SUBKIND_LS_KEY = "fromhere_gig_subkind_v1";
const GIG_TIME_LS_KEY = "fromhere_gig_time_v1";
const OPEN_NOW_LS_KEY = "fromhere_open_now_v1";
const SORT_LS_KEY = "fromhere_sort_v1";
// Persist the "where am I exploring from?" state so a user leaving the map to visit a
// listing doesn't lose their scout pin or from-me mode when they come back.
const SCOUT_PIN_LS_KEY = "fromhere_scout_pin_v1";
const FROM_ME_LS_KEY = "fromhere_from_me_v1";
const DEFAULT_ACTIVE_KINDS = { produce: true, eat_drink: true, shop: true, services: true, beauty: true, venue: true, creator: true, event: true, photo: true, gig: true };

function loadActiveKinds() {
    try {
        const raw = localStorage.getItem(LAYER_LS_KEY);
        if (!raw) {
            // Migrate the old v1 key (single `business` layer) into the new 3 sub-layers.
            const legacy = localStorage.getItem("fromhere_active_kinds_v1");
            if (legacy) {
                try {
                    const parsed = JSON.parse(legacy);
                    const businessOn = parsed.business !== false;
                    return {
                        ...DEFAULT_ACTIVE_KINDS,
                        eat_drink: businessOn,
                        shop:      businessOn,
                        services:  businessOn,
                        creator: parsed.creator !== false,
                        event:   parsed.event !== false,
                        photo:   parsed.photo !== false,
                        gig:     parsed.gig !== false,
                    };
                } catch {}
            }
            return DEFAULT_ACTIVE_KINDS;
        }
        const parsed = JSON.parse(raw);
        return { ...DEFAULT_ACTIVE_KINDS, ...parsed };
    } catch { return DEFAULT_ACTIVE_KINDS; }
}

function loadGigSubKind() {
    try { return localStorage.getItem(GIG_SUBKIND_LS_KEY) || "all"; } catch { return "all"; }
}
function loadGigTime() {
    try { return localStorage.getItem(GIG_TIME_LS_KEY) || "all"; } catch { return "all"; }
}
function loadOpenNow() {
    try { return localStorage.getItem(OPEN_NOW_LS_KEY) === "1"; } catch { return false; }
}
function loadSort() {
    try { return localStorage.getItem(SORT_LS_KEY) || "nearest"; } catch { return "nearest"; }
}
function loadScoutPin() {
    try {
        const raw = localStorage.getItem(SCOUT_PIN_LS_KEY);
        if (!raw) return null;
        const p = JSON.parse(raw);
        if (typeof p.lat === "number" && typeof p.lng === "number") return p;
        return null;
    } catch { return null; }
}
function loadFromMe() {
    try { return localStorage.getItem(FROM_ME_LS_KEY) === "1"; } catch { return false; }
}

// "Open now" utility — evaluates hours[] (`[{day, open, close}]`) against the current time.
// Rows with empty open/close are treated as "closed all day".
function isOpenNow(hours) {
    if (!Array.isArray(hours) || hours.length === 0) return false;
    const now = new Date();
    const dayLbl = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][now.getDay()];
    const row = hours.find((h) => h.day === dayLbl);
    if (!row || !row.open || !row.close) return false;
    const hm = (s) => { const [h, m] = String(s).split(":").map(Number); return h * 60 + (m || 0); };
    const cur = now.getHours() * 60 + now.getMinutes();
    return cur >= hm(row.open) && cur <= hm(row.close);
}

// Five discovery layers on the map. Note: the DB still stores kind = "artist" | "maker"
// as separate values for category taxonomy — but from the user's perspective they're
// one merged "Makers & Artists" layer (the distinction was too thin to justify two rows).
// Every pin resolves to one PRIMARY layer via its kind; sellers can add optional
// `also_show_in` layer keys so a maker with a shop shows up in Businesses too.
// Display-layer taxonomy. `business` kind fans out into three intent-based layers
// via category (Eat & Drink / Shops / Services & Wellness) so the map doesn't feel
// like a catch-all. Coworking rolls into Makers & Artists (creative workspace).
const KIND_META = {
    eat_drink: { label: "Eat & Drink",         color: "marker-eat",      Icon: Coffee,       hex: "hsl(27 87% 55%)" },
    shop:      { label: "Shops",               color: "marker-shop",     Icon: ShoppingBag,  hex: "hsl(131 22% 29%)" },
    services:  { label: "Services",            color: "marker-services", Icon: Wrench,       hex: "hsl(180 45% 40%)" },
    beauty:    { label: "Health & Beauty",     color: "marker-beauty",   Icon: Heart,        hex: "hsl(330 55% 52%)" },
    produce:   { label: "Farms & Producers",   color: "marker-produce",  Icon: Wheat,        hex: "hsl(85 40% 34%)" },
    venue:     { label: "Venues & Spaces",     color: "marker-venue",    Icon: Landmark,     hex: "hsl(260 45% 50%)" },
    creator:   { label: "Makers & Artists",    color: "marker-creator",  Icon: Palette,      hex: "hsl(20 78% 60%)" },
    event:     { label: "Markets & events",    color: "marker-event",    Icon: CalendarIcon, hex: "hsl(220 55% 45%)" },
    gig:       { label: "Gigs & shows",        color: "marker-gig",      Icon: Music,        hex: "hsl(340 82% 55%)" },
};

import { businessSubLayer, resolvePrimaryLayer, KIND_TO_LAYER } from "@/lib/taxonomy";
import { thumb } from "@/lib/img";
import { FoundingBadge } from "@/components/FoundingBadge";
import { FollowPinButton } from "@/components/FollowPinButton";

// Legacy alias kept for any direct call-sites below.

// Which display layers does this pin belong to? Union of primary + optional also_show_in.
function pinLayers(l) {
    const primary = resolvePrimaryLayer(l);
    const secondary = Array.isArray(l.also_show_in) ? l.also_show_in : [];
    return primary ? [primary, ...secondary] : secondary;
}

export default function Home() {
    const [listings, setListings] = useState([]);
    const [refreshing, setRefreshing] = useState(false);
    const [cachedAt, setCachedAt] = useState(null);
    const [photos, setPhotos] = useState([]);
    const [gigs, setGigs] = useState([]);
    const [tonightPinIds, setTonightPinIds] = useState(() => new Set());
    const [shared] = useState(() => {
        const p = new URLSearchParams(window.location.search);
        const lat = parseFloat(p.get("lat")), lng = parseFloat(p.get("lng"));
        if (!p.get("q") && Number.isNaN(lat)) return null;
        try { sessionStorage.removeItem("fromhere_map_view_v1"); } catch {}
        return { q: p.get("q") || "", lat: Number.isNaN(lat) ? null : lat, lng: Number.isNaN(lng) ? null : lng, r: parseFloat(p.get("r")) || null };
    });
    const [q, setQ] = useState(() => { if (shared) return shared.q; try { return sessionStorage.getItem("fromhere_search_q") || ""; } catch { return ""; } });
    const [searchFlash, setSearchFlash] = useState(false);
    const [showEnterHint, setShowEnterHint] = useState(false);
    const [fitTarget, setFitTarget] = useState(null);
    const [recent, setRecent] = useState(() => { try { return JSON.parse(localStorage.getItem("fromhere_recent_searches_v1") || "[]"); } catch { return []; } });
    useEffect(() => { try { sessionStorage.setItem("fromhere_search_q", q); } catch {} }, [q]);
    const rememberSearch = (term) => {
        const t = term.trim();
        if (!t) return;
        const next = [t, ...recent.filter((r) => r.toLowerCase() !== t.toLowerCase())].slice(0, 5);
        setRecent(next);
        try { localStorage.setItem("fromhere_recent_searches_v1", JSON.stringify(next)); } catch {}
        if (originRef.current && t.length >= 2) api.post("/search-log", { q: t, ...originRef.current }).catch(() => {});
    };
    const originRef = useRef(null);
    const [popular, setPopular] = useState([]);
    const [allCategories, setAllCategories] = useState([]);
    useEffect(() => {
        api.get("/categories").then(({ data }) => setAllCategories([...new Set(Object.values(data).flat())])).catch(() => {});
    }, []);
    const clearRecent = () => { setRecent([]); try { localStorage.removeItem("fromhere_recent_searches_v1"); } catch {} };

    const flashResults = () => {
        setSearchFlash(true);
        // Match the animation duration in index.css (.results-flash-green)
        setTimeout(() => setSearchFlash(false), 2100);
    };
    const [activeKinds, setActiveKinds] = useState(loadActiveKinds);
    const [gigSubKind, setGigSubKind] = useState(loadGigSubKind); // all|music|quiz|art|performance|comedy
    const [gigTime, setGigTime] = useState(loadGigTime);           // all|now|today|week
    const [openNow, setOpenNow] = useState(loadOpenNow);           // businesses "open now" toggle
    const [sortBy, setSortBy] = useState(loadSort);                // nearest | newest | rated

    // Persist layer + subkind preferences so returning users see the same layers they left with.
    useEffect(() => {
        try { localStorage.setItem(LAYER_LS_KEY, JSON.stringify(activeKinds)); } catch {}
    }, [activeKinds]);
    useEffect(() => {
        try { localStorage.setItem(GIG_SUBKIND_LS_KEY, gigSubKind); } catch {}
    }, [gigSubKind]);
    useEffect(() => { try { localStorage.setItem(GIG_TIME_LS_KEY, gigTime); } catch {} }, [gigTime]);
    useEffect(() => { try { localStorage.setItem(OPEN_NOW_LS_KEY, openNow ? "1" : "0"); } catch {} }, [openNow]);
    useEffect(() => { try { localStorage.setItem(SORT_LS_KEY, sortBy); } catch {} }, [sortBy]);
    const [selected, setSelected] = useState(null);
    const [selectedPhoto, setSelectedPhoto] = useState(null);
    const [selectedGig, setSelectedGig] = useState(null);
    const [fromMe, setFromMe] = useState(loadFromMe);
    const [pinOpen, setPinOpen] = useState(false);
    const [scoutMode, setScoutMode] = useState(false);
    const [scoutPin, setScoutPin] = useState(loadScoutPin); // { lat, lng, display_name } — persisted so it survives navigation
    // Scout-pin visibility: distracting once the user has settled on a radius,
    // so it defaults to hidden. Users can re-show it from the Where menu.
    const [showScoutMarker, setShowScoutMarker] = useState(false);
    // Radius panel also collapses to a tiny pill after 3 seconds of no slider
    // interaction, so it doesn't blanket the map. User can tap the pill to re-open.
    const [radiusPanelOpen, setRadiusPanelOpen] = useState(() => window.innerWidth >= 640);
    const radiusCollapseTimer = useRef(null);
    const scheduleRadiusCollapse = () => {
        clearTimeout(radiusCollapseTimer.current);
        setRadiusPanelOpen(true);
        radiusCollapseTimer.current = setTimeout(() => setRadiusPanelOpen(false), 3000);
    };
    // Persist Where-mode state so navigating to a listing and back keeps the pin.
    useEffect(() => {
        try {
            if (scoutPin) localStorage.setItem(SCOUT_PIN_LS_KEY, JSON.stringify(scoutPin));
            else localStorage.removeItem(SCOUT_PIN_LS_KEY);
        } catch {}
    }, [scoutPin]);
    useEffect(() => {
        try { localStorage.setItem(FROM_ME_LS_KEY, fromMe ? "1" : "0"); } catch {}
    }, [fromMe]);
    const [tripsOpen, setTripsOpen] = useState(false);
    const [exploreLocOpen, setExploreLocOpen] = useState(false);
    // Jump-to-location target when the user picks a place from Explore-a-location
    const [flyTarget, setFlyTarget] = useState(null);
    const [sheetOpen, setSheetOpen] = useState(false);
    const navigate = useNavigate();
    const { user } = useAuth();
    const { pos, error: geoError, loading: geoLoading, request: requestPos } = useGeolocation({ watch: fromMe });

    const homePos = user?.home_lat && user?.home_lng && (user?.filter_radius_km || 0) > 0
        ? { lat: user.home_lat, lng: user.home_lng } : null;
    const [radius, setRadius] = useState(user?.filter_radius_km || 5);
    useEffect(() => { if (user?.filter_radius_km && !shared?.r) setRadius(user.filter_radius_km); }, [user?.filter_radius_km]); // eslint-disable-line react-hooks/exhaustive-deps
    // Opened from a shared search link: explore that spot, then tidy the address bar.
    useEffect(() => {
        if (!shared) return;
        if (shared.lat != null) setScoutPin({ lat: shared.lat, lng: shared.lng, display_name: "Shared search" });
        if (shared.r) setRadius(shared.r);
        navigate("/", { replace: true });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // origin priority: scout > from-me > home
    const origin = scoutPin ? { lat: scoutPin.lat, lng: scoutPin.lng } : (fromMe ? pos : homePos);
    const originLabel = scoutPin ? "Scouting" : (fromMe ? "You are here" : "Home");
    const radiusActive = !!origin;
    originRef.current = origin ? { lat: origin.lat, lng: origin.lng } : null;
    const areaKey = origin ? `${origin.lat.toFixed(2)},${origin.lng.toFixed(2)}` : "";
    useEffect(() => {
        if (!areaKey) { setPopular([]); return; }
        const [lat, lng] = areaKey.split(",");
        api.get(`/search-popular?lat=${lat}&lng=${lng}`).then(({ data }) => setPopular(data)).catch(() => setPopular([]));
    }, [areaKey]);

    const handleMapClick = async ({ lat, lng, scoutMode: clickInScoutMode }) => {
        // First priority: if a pin/photo/gig card is open, tapping the map dismisses it.
        if (selected || selectedPhoto || selectedGig) {
            setSelected(null); setSelectedPhoto(null); setSelectedGig(null);
            return;
        }
        // Otherwise, only drop a scout pin if the user is explicitly in scout mode.
        if (!clickInScoutMode) return;
        setScoutPin({ lat, lng, display_name: `${lat.toFixed(4)}, ${lng.toFixed(4)}` });
        setScoutMode(false); // one-shot drop; user can re-enter to move
        // Flash the pin marker for 3s so the user sees where it landed, then auto-hide.
        setShowScoutMarker(true);
        setTimeout(() => setShowScoutMarker(false), 3000);
        // Also open the radius panel briefly so they can pick a radius, then auto-collapse.
        scheduleRadiusCollapse();
        try {
            const { data } = await api.get(`/reverse-geocode?lat=${lat}&lng=${lng}`);
            setScoutPin({ lat, lng, display_name: data.display_name });
        } catch { /* keep coords */ }
    };

    useEffect(() => {
        // Paint the pins from the last visit straight away, then refresh quietly.
        const cached = readMapCache();
        if (cached) { setListings(cached.listings); setPhotos(cached.photos); setGigs(cached.gigs); setCachedAt(cached.ts); }
        (async () => {
            setRefreshing(true);
            try {
                const [l, p, g] = await Promise.all([
                    api.get("/listings"),
                    Promise.resolve({ data: [] }), // Photo spots layer retired (data kept server-side)
                    // since_hours=4 so gigs currently in progress (started up to 4h ago) still
                    // reach the map — required for the "On now" chip to have anything to show.
                    api.get("/gigs?since_hours=4"),
                ]);
                setListings(l.data);
                setPhotos(p.data);
                setGigs(g.data);
                setCachedAt(null);
                writeMapCache({ listings: l.data, photos: p.data, gigs: g.data, ts: Date.now() });
            } catch (e) {
                if (!cached || e?.response) toast.error(apiError(e));
            } finally { setRefreshing(false); }
            try {
                const { data } = await api.get("/whats-on/tonight");
                setTonightPinIds(new Set(data.listing_ids || []));
            } catch {}
        })();
    }, []);

    const filteredListings = useMemo(() => {
        if (q && !radiusActive) return [];
        return listings.filter((l) => {
            // A pin can belong to multiple layers (its primary + any also_show_in) — keep it
            // if ANY of those layers is toggled on.
            const layers = pinLayers(l);
            if (!layers.some((lay) => activeKinds[lay])) return false;
            if (radiusActive && distanceKm(origin, { lat: l.lat, lng: l.lng }) > radius) return false;
            // "Open now" only makes sense for businesses (other pin kinds don't publish hours).
            if (openNow && l.kind === "business" && !isOpenNow(l.hours)) return false;
            if (q) {
                const s = q.toLowerCase();
                return (l.name?.toLowerCase().includes(s) ||
                        l.description?.toLowerCase().includes(s) ||
                        l.category?.toLowerCase().includes(s) ||
                        (l.tags || []).some((t) => t.toLowerCase().includes(s)));
            }
            return true;
        });
    }, [listings, activeKinds, q, radiusActive, origin, radius, openNow]);

    const filteredPhotos = useMemo(() => {
        if (!activeKinds.photo) return [];
        if (q && !radiusActive) return [];
        return photos.filter((p) => {
            if (radiusActive && distanceKm(origin, { lat: p.lat, lng: p.lng }) > radius) return false;
            if (!q) return true;
            const s = q.toLowerCase();
            return (p.title?.toLowerCase().includes(s) ||
                    p.description?.toLowerCase().includes(s) ||
                    p.location_name?.toLowerCase().includes(s) ||
                    p.photographer_name?.toLowerCase().includes(s) ||
                    (p.tags || []).some((t) => t.toLowerCase().includes(s)));
        });
    }, [photos, activeKinds, q, radiusActive, origin, radius]);

    const filteredGigs = useMemo(() => {
        if (!activeKinds.gig) return [];
        if (q && !radiusActive) return [];
        // Precompute the time window based on the picked chip.
        const now = new Date();
        const endOfToday = new Date(now); endOfToday.setHours(23, 59, 59, 999);
        const weekAhead = new Date(now.getTime() + 7 * 86400000);
        return gigs.filter((g) => {
            if (gigSubKind !== "all" && g.kind !== gigSubKind) return false;
            if (radiusActive && distanceKm(origin, { lat: g.lat, lng: g.lng }) > radius) return false;
            if (gigTime !== "all" && g.start_at) {
                const start = new Date(g.start_at);
                if (gigTime === "now") {
                    // "On now" — started within the last 3h and not further than 30min ahead.
                    const window = 3 * 3600 * 1000;
                    if (start.getTime() > now.getTime() + 30 * 60 * 1000) return false;
                    if (start.getTime() < now.getTime() - window) return false;
                } else if (gigTime === "today") {
                    if (start > endOfToday || start.toDateString() !== now.toDateString()) return false;
                } else if (gigTime === "week") {
                    if (start > weekAhead || start < now) return false;
                }
            }
            if (!q) return true;
            const s = q.toLowerCase();
            return (g.title?.toLowerCase().includes(s) ||
                    g.description?.toLowerCase().includes(s) ||
                    g.kind?.toLowerCase().includes(s) ||
                    g.performer_name?.toLowerCase().includes(s) ||
                    g.address?.toLowerCase().includes(s) ||
                    (g.tags || []).some((t) => t.toLowerCase().includes(s)));
        });
    }, [gigs, activeKinds, q, radiusActive, origin, radius, gigSubKind, gigTime]);

    const toggle = (k) => setActiveKinds((p) => ({ ...p, [k]: !p[k] }));
    const totalCount = filteredListings.length + filteredPhotos.length + filteredGigs.length;
    const mapMatches = filteredListings.length + filteredGigs.length;
    const suggestions = useMemo(() => {
        const s = q.trim().toLowerCase();
        if (s.length < 2) return [];
        const counts = {};
        const inReach = listings.filter((l) => !radiusActive || distanceKm(origin, { lat: l.lat, lng: l.lng }) <= radius);
        const hay = inReach.map((l) => [l.name, l.description, l.category, ...(l.tags || [])].filter(Boolean).map((f) => String(f).toLowerCase()));
        const matches = allCategories.filter((c) => c.toLowerCase().includes(s) && c.toLowerCase() !== s);
        for (const c of matches) counts[c] = hay.filter((fields) => fields.some((f) => f.includes(c.toLowerCase()))).length;
        return matches
            .map((c) => ({ name: c, count: counts[c] || 0 }))
            .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))
            .slice(0, 5);
    }, [q, allCategories, listings, radiusActive, origin, radius]);
    const showOnMap = () => {
        const points = [...filteredListings, ...filteredGigs];
        if (!points.length) return;
        rememberSearch(q);
        setSheetOpen(false);
        setFitTarget({ points, key: Date.now() });
    };

    // Sort helpers for the cards in the bottom sheet — pin markers themselves stay geographic.
    const dist = useCallback((it) => origin ? distanceKm(origin, { lat: it.lat, lng: it.lng }) : 0, [origin]);
    const sortFn = useCallback((a, b) => {
        if (sortBy === "newest") return String(b.created_at || "").localeCompare(String(a.created_at || ""));
        if (sortBy === "rated") return (b.rating_avg || 0) - (a.rating_avg || 0);
        return dist(a) - dist(b); // nearest (default)
    }, [sortBy, dist]);
    const sortedListings = useMemo(() => [...filteredListings].sort(sortFn), [filteredListings, sortFn]);
    const sortedPhotos = useMemo(() => [...filteredPhotos].sort(sortFn), [filteredPhotos, sortFn]);
    const sortedGigs = useMemo(() => [...filteredGigs].sort(sortFn), [filteredGigs, sortFn]);

    // ─── "Further afield" — matches outside the radius (2026-09-16) ───
    // A visitor searching "The Fort Theatre" from a home pin in Bristol shouldn't get zero
    // results just because it's in Glasgow. When the user has typed a query AND a radius is
    // set, surface up to 30 matches beyond the radius in their own section below the
    // in-radius list. Empty search stays radius-scoped so casual browsing isn't diluted.
    const outOfRadius = useMemo(() => {
        if (!q.trim() || !radiusActive) return { listings: [], photos: [], gigs: [] };
        const s = q.toLowerCase();
        const beyond = (item) => distanceKm(origin, { lat: item.lat, lng: item.lng }) > radius;
        const matchListing = (l) => (
            l.name?.toLowerCase().includes(s) ||
            l.description?.toLowerCase().includes(s) ||
            l.category?.toLowerCase().includes(s) ||
            (l.tags || []).some((t) => t.toLowerCase().includes(s))
        );
        const matchPhoto = (p) => (
            p.title?.toLowerCase().includes(s) ||
            p.description?.toLowerCase().includes(s) ||
            p.location_name?.toLowerCase().includes(s) ||
            p.photographer_name?.toLowerCase().includes(s) ||
            (p.tags || []).some((t) => t.toLowerCase().includes(s))
        );
        const matchGig = (g) => (
            g.title?.toLowerCase().includes(s) ||
            g.description?.toLowerCase().includes(s) ||
            g.kind?.toLowerCase().includes(s) ||
            g.performer_name?.toLowerCase().includes(s) ||
            g.address?.toLowerCase().includes(s)
        );
        const byDist = (a, b) => dist(a) - dist(b);
        return {
            listings: listings.filter(beyond).filter(matchListing).sort(byDist).slice(0, 30),
            photos: photos.filter(beyond).filter(matchPhoto).sort(byDist).slice(0, 30),
            gigs: gigs.filter(beyond).filter(matchGig).sort(byDist).slice(0, 30),
        };
    }, [q, radiusActive, origin, radius, listings, photos, gigs, dist]);
    const outOfRadiusCount = outOfRadius.listings.length + outOfRadius.photos.length + outOfRadius.gigs.length;

    // Trending "near you" — top 3 by (saves + orders + freshness). Fully client-side, no
    // extra backend calls; freshness bumps anything created in the last 14 days so brand-new
    // pins get an initial boost even without saves.
    const trending = useMemo(() => {
        if (!radiusActive) return [];
        const now = Date.now();
        const freshBoost = (it) => {
            if (!it.created_at) return 0;
            const ageDays = (now - new Date(it.created_at).getTime()) / 86400000;
            return ageDays < 14 ? Math.max(0, 3 - ageDays / 5) : 0;
        };
        const score = (it) => (it.save_count || 0) * 2 + (it.order_count || 0) * 3 + freshBoost(it);
        return [...filteredListings, ...filteredPhotos, ...filteredGigs]
            .map((it) => ({ it, s: score(it) }))
            .filter((x) => x.s > 0)
            .sort((a, b) => b.s - a.s)
            .slice(0, 4)
            .map((x) => x.it);
    }, [filteredListings, filteredPhotos, filteredGigs, radiusActive]);

    // Radius/search-aware counts for the filter chips (ignoring the on/off toggle
    // so numbers reflect what's *reachable* right now, not what's currently visible).
    const inRadius = (item) => !radiusActive || distanceKm(origin, { lat: item.lat, lng: item.lng }) <= radius;
    const matchesQ = (fields) => {
        if (!q) return true;
        const s = q.toLowerCase();
        return fields.filter(Boolean).some((f) => String(f).toLowerCase().includes(s));
    };
    const listingsInReach = useMemo(() => listings.filter(inRadius).filter((l) =>
        matchesQ([l.name, l.description, l.category, ...(l.tags || [])])
    ), [listings, radiusActive, origin, radius, q]);
    const photosInReach = useMemo(() => photos.filter(inRadius).filter((p) =>
        matchesQ([p.title, p.description, p.location_name, p.photographer_name, ...(p.tags || [])])
    ), [photos, radiusActive, origin, radius, q]);
    const gigsInReach = useMemo(() => gigs.filter(inRadius).filter((g) =>
        matchesQ([g.title, g.description, g.kind, g.performer_name, g.address])
    ), [gigs, radiusActive, origin, radius, q]);
    const chipCount = (k) => {
        if (k === "photo") return photosInReach.length;
        if (k === "gig") {
            // Apply the same time-window filter used in filteredGigs so the chip count
            // matches what actually renders on the map.
            const now = new Date();
            const endOfToday = new Date(now); endOfToday.setHours(23, 59, 59, 999);
            const weekAhead = new Date(now.getTime() + 7 * 86400000);
            const inTime = (g) => {
                if (gigTime === "all" || !g.start_at) return true;
                const start = new Date(g.start_at);
                if (gigTime === "now") {
                    const w = 3 * 3600 * 1000;
                    return start.getTime() <= now.getTime() + 30 * 60 * 1000
                        && start.getTime() >= now.getTime() - w;
                }
                if (gigTime === "today") return start.toDateString() === now.toDateString();
                if (gigTime === "week") return start >= now && start <= weekAhead;
                return true;
            };
            const pool = gigsInReach.filter(inTime);
            return gigSubKind === "all"
                ? pool.length
                : pool.filter((g) => g.kind === gigSubKind).length;
        }
        if ((k === "eat_drink" || k === "shop" || k === "services" || k === "beauty" || k === "venue" || k === "produce") && openNow) {
            return listingsInReach.filter((l) => pinLayers(l).includes(k) && isOpenNow(l.hours)).length;
        }
        // A pin counts toward the layer if it's the primary OR listed in also_show_in.
        return listingsInReach.filter((l) => pinLayers(l).includes(k)).length;
    };

    const [geoPrePromptOpen, setGeoPrePromptOpen] = useState(false);
    const [tourDone, setTourDone] = useState(() => {
        try { return !!localStorage.getItem("fromhere_tour_v1"); } catch { return false; }
    });
    // Listen for the tour dismissal so the geo nudge can fire in the same session.
    useEffect(() => {
        const onTourDone = () => setTourDone(true);
        window.addEventListener("fromhere:tour-done", onTourDone);
        return () => window.removeEventListener("fromhere:tour-done", onTourDone);
    }, []);
    // First-visit geolocation nudge — only for signed-in users without a home pin, AND only
    // after the onboarding tour has been dismissed (otherwise the second Dialog's overlay
    // covers the tour and blocks all pointer events on the page).
    useEffect(() => {
        if (!user) return; // Never nudge anonymous visitors — they don't have a preferences endpoint.
        if (!tourDone) return;
        try {
            const asked = localStorage.getItem("fromhere_geo_ack_v1");
            const nudged = localStorage.getItem("fromhere_geo_nudge_v1");
            if (!asked && !nudged && !homePos && !fromMe && !scoutPin) {
                const t = setTimeout(() => {
                    setGeoPrePromptOpen(true);
                    try { localStorage.setItem("fromhere_geo_nudge_v1", "1"); } catch {}
                }, 700);
                return () => clearTimeout(t);
            }
        } catch { /* private mode */ }
    }, [user, homePos, fromMe, scoutPin, tourDone]);

    const toggleFromMe = () => {
        if (fromMe) { setFromMe(false); return; }
        // Show a plain-English pre-prompt once per browser so users know why we're asking
        // before the OS permission dialog appears. Improves accept rates + is GDPR-friendly.
        try {
            if (localStorage.getItem("fromhere_geo_ack_v1")) {
                setFromMe(true); requestPos(); return;
            }
        } catch { /* private mode */ }
        setGeoPrePromptOpen(true);
    };
    const confirmGeoPrompt = () => {
        try { localStorage.setItem("fromhere_geo_ack_v1", "1"); } catch {}
        setGeoPrePromptOpen(false);
        setFromMe(true);
        requestPos();
    };
    useEffect(() => {
        if (fromMe && geoError) { toast.error(geoError); setFromMe(false); }
    }, [geoError, fromMe]);

    // Search now filters pins by title / description / tags within the active radius only —
    // the old geocode dropdown moved to the Explore ▸ Explore a location dialog.

    const onFlyToDone = useCallback(() => setFlyTarget(null), []);

    // Share this map view — bakes filter/radius/origin into a URL so someone can open the same view.
    const shareView = async () => {
        const params = new URLSearchParams();
        if (q) params.set("q", q);
        if (origin) { params.set("lat", origin.lat.toFixed(4)); params.set("lng", origin.lng.toFixed(4)); }
        if (radiusActive) params.set("r", String(radius));
        const url = `${window.location.origin}/s?${params.toString()}`;
        const title = q ? `"${q}" on From Here` : "From Here map";
        try {
            if (navigator.share) {
                await navigator.share({ title, url });
            } else {
                await navigator.clipboard.writeText(url);
                toast.success(q ? "Search link copied" : "Map link copied");
            }
        } catch { /* user cancelled share */ }
    };

    // Callback for Explore ▸ Explore a location — drops a scout pin at the picked place, flies there,
    // and opens the Trips dialog so signed-in users get a one-click "save this spot as a trip" prompt.
    const handleExploreLocationPick = ({ lat, lng, display_name }) => {
        setScoutPin({ lat, lng, display_name });
        setScoutMode(false);
        setFromMe(false);
        setFlyTarget({ lat, lng, zoom: 13, key: Date.now() });
        // Flash the pin marker for 3s so the user sees where it landed, then auto-hide.
        setShowScoutMarker(true);
        setTimeout(() => setShowScoutMarker(false), 3000);
        // Also open the radius panel briefly so they can pick a radius, then auto-collapse.
        scheduleRadiusCollapse();
        toast(`Exploring ${display_name.split(",").slice(0, 2).join(",")}`);
        if (user) {
            // Small delay so the map fly animation starts first — the Trips dialog then appears
            // over the top with the "Save trip" form pre-filled to the picked place.
            setTimeout(() => setTripsOpen(true), 300);
        }
    };

    return (
        <div className="relative h-[calc(100vh-4rem)] w-full overflow-hidden">
            <MapFreshness refreshing={refreshing} cachedAt={cachedAt} />
            <div className="absolute inset-0">
                <MapView
                    listings={filteredListings}
                    photos={[]}
                    gigs={filteredGigs}
                    origin={radiusActive ? origin : null}
                    originLabel={originLabel}
                    radiusKm={radiusActive ? radius : 0}
                    onSelect={(l) => { setSelectedPhoto(null); setSelectedGig(null); setSelected(l); }}
                    onSelectPhoto={(p) => { setSelected(null); setSelectedGig(null); setSelectedPhoto(p); }}
                    onSelectGig={(g) => { setSelected(null); setSelectedPhoto(null); setSelectedGig(g); }}
                    onMapClick={handleMapClick}
                    scoutMode={scoutMode}
                    flyToTarget={flyTarget}
                    onFlyToDone={onFlyToDone}
                    tonightPinIds={tonightPinIds}
                    showOriginMarker={scoutPin ? showScoutMarker : true}
                    selectedKey={selected ? `listing:${selected.listing_id}` : selectedPhoto ? `photo:${selectedPhoto.photo_id}` : selectedGig ? `gig:${selectedGig.gig_id}` : null}
                    fitTarget={fitTarget}
                />
            </div>

            <div className="absolute top-4 left-4 right-4 z-[500] flex flex-col gap-3 pointer-events-none">
                <div className="relative max-w-2xl pointer-events-auto">
                    <div className="glass rounded-full shadow-lg px-2 py-1.5 flex items-center gap-2">
                        <Search size={18} className="text-muted-foreground ml-3" />
                        <Input
                            data-testid="search-input"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            onFocus={() => setShowEnterHint(true)}
                            onBlur={() => setTimeout(() => setShowEnterHint(false), 150)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && q.trim()) {
                                    e.preventDefault();
                                    flashResults();
                                    showOnMap();
                                    // Just blur so mobile keyboards close — the results button
                                    // is already pinned to the bottom of the map, so no page
                                    // scroll is needed (was pushing the top bar off-screen).
                                    e.currentTarget.blur();
                                }
                            }}
                            placeholder={radiusActive ? "Search nearby…" : "Set your home to search"}
                            className="border-0 focus-visible:ring-0 shadow-none bg-transparent text-base"
                        />
                        {q && showEnterHint && (
                            <kbd data-testid="search-enter-hint"
                                className="hidden sm:inline-flex items-center gap-1 px-2 py-0.5 rounded border border-stone-300 bg-white text-[10px] uppercase tracking-widest font-bold text-muted-foreground shadow-sm">
                                ⏎ Enter
                            </kbd>
                        )}
                        {q && (
                            <button onClick={() => setQ("")} className="p-2 hover:text-accent" data-testid="clear-search">
                                <X size={16} />
                            </button>
                        )}
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button data-testid="sort-toggle"
                                    className="hidden sm:inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                                    title="Sort results">
                                    <ArrowUpDown size={12} /> {SORT_LABELS[sortBy]}
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                                <DropdownMenuLabel>Sort results by</DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                {Object.entries(SORT_LABELS).map(([v, label]) => (
                                    <DropdownMenuItem key={v}
                                        data-testid={`sort-${v}`}
                                        onClick={() => setSortBy(v)}
                                        className={sortBy === v ? "font-bold text-accent" : ""}
                                    >{label}</DropdownMenuItem>
                                ))}
                            </DropdownMenuContent>
                        </DropdownMenu>
                        <button data-testid="share-view-btn" onClick={shareView}
                            title={q ? "Share this search" : "Share this map view"}
                            className={`${q ? "inline-flex" : "hidden sm:inline-flex"} items-center justify-center w-9 h-9 shrink-0 rounded-full text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors mr-1`}>
                            <Share2 size={14} />
                        </button>
                    </div>
                    {!q && showEnterHint && (recent.length > 0 || popular.length > 0) && (
                        <div data-testid="recent-searches" className="mt-2 mx-2 px-3 py-2 rounded-2xl glass shadow-lg space-y-1.5 text-xs">
                            {recent.length > 0 && (
                                <div className="flex items-center gap-1.5 flex-wrap">
                                    <span className="font-bold text-muted-foreground mr-1">Recent</span>
                                    {recent.map((r) => (
                                        <button key={r} onMouseDown={(e) => e.preventDefault()} onClick={() => { setQ(r); flashResults(); }}
                                            className="px-2.5 py-1 rounded-full bg-white border border-stone-200 hover:border-primary transition-colors" data-testid={`recent-search-${r}`}>
                                            {r}
                                        </button>
                                    ))}
                                    <button onMouseDown={(e) => e.preventDefault()} onClick={clearRecent} className="ml-auto text-muted-foreground underline" data-testid="recent-searches-clear">Clear</button>
                                </div>
                            )}
                            {popular.length > 0 && (
                                <div className="flex items-center gap-1.5 flex-wrap" data-testid="popular-searches">
                                    <span className="font-bold text-muted-foreground mr-1 inline-flex items-center gap-1"><TrendingUp size={11} className="text-accent" /> Popular here</span>
                                    {popular.map((r) => (
                                        <button key={r} onMouseDown={(e) => e.preventDefault()} onClick={() => { setQ(r); flashResults(); }}
                                            className="px-2.5 py-1 rounded-full bg-accent/10 border border-accent/30 hover:border-accent transition-colors" data-testid={`popular-search-${r}`}>
                                            {r}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                    {q.trim().length >= 2 && showEnterHint && suggestions.length > 0 && (
                        <div data-testid="search-suggestions" className="mt-2 mx-2 py-1 rounded-2xl glass shadow-lg text-sm max-w-md">
                            {suggestions.map((s) => (
                                <button key={s.name} onMouseDown={(e) => e.preventDefault()} onClick={() => { setQ(s.name); flashResults(); }}
                                    className="w-full text-left px-4 py-1.5 hover:bg-secondary/70 flex items-center gap-2" data-testid={`search-suggestion-${s.name}`}>
                                    <Search size={12} className="text-muted-foreground" /> {s.name}
                                    <span className="ml-auto text-xs text-muted-foreground">{s.count ? `${s.count} nearby` : "none nearby yet"}</span>
                                </button>
                            ))}
                        </div>
                    )}
                    {q && radiusActive && mapMatches > 0 && (
                        <button onClick={showOnMap} data-testid="search-show-on-map"
                            className="mt-2 mx-2 px-4 py-1.5 rounded-full glass shadow text-xs font-bold inline-flex items-center gap-1.5 hover:-translate-y-0.5 transition-transform">
                            <MapPin size={12} className="text-primary" /> Show {mapMatches} {mapMatches === 1 ? "match" : "matches"} on map
                        </button>
                    )}
                    {q && radiusActive && mapMatches === 0 && (
                        <div data-testid="search-no-matches" className="mt-2 mx-2 px-4 py-2 text-xs rounded-2xl glass shadow">
                            No matches for "{q}" in your {radius} km radius.{outOfRadiusCount > 0 ? ` ${outOfRadiusCount} further afield, open the results to see them.` : " Try a shorter word, or widen your radius."}
                        </div>
                    )}
                    {q && !radiusActive && (
                        <div data-testid="search-no-radius-hint" className="mt-2 mx-2 px-4 py-2 text-xs rounded-2xl bg-amber-50 border border-amber-200 text-amber-900">
                            Search only looks inside your radius. Set your <button onClick={() => setPinOpen(true)} className="underline font-bold">home pin</button> or use <span className="font-bold">Explore</span> to drop an explore pin first.
                        </div>
                    )}
                </div>
                <div className="flex flex-nowrap sm:flex-wrap gap-2 pointer-events-auto overflow-x-auto -mx-4 px-4 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                    {/* Where — burger-style menu replacing From me / Explore / Trips / Set home */}
                    <WhereMenu
                        user={user}
                        fromMe={fromMe}
                        scoutMode={scoutMode}
                        scoutPin={scoutPin}
                        showScoutMarker={showScoutMarker}
                        homePos={homePos}
                        geoLoading={geoLoading}
                        onFromMe={toggleFromMe}
                        onSetHomePin={() => setPinOpen(true)}
                        onExploreAddress={() => setExploreLocOpen(true)}
                        onDropPin={() => { setScoutMode(true); toast("Tap anywhere on the map to drop an explore pin"); }}
                        onOpenTrips={() => setTripsOpen(true)}
                        onClearScout={() => { setScoutPin(null); setShowScoutMarker(false); }}
                        onToggleScoutMarker={() => setShowScoutMarker(v => !v)}
                    />
                    {/* Layers — burger-style menu replacing the six per-kind chips + gig-type + open-now */}
                    <LayersMenu
                        KIND_META={KIND_META}
                        activeKinds={activeKinds}
                        onToggleKind={toggle}
                        chipCount={chipCount}
                        GIG_SUB_LABELS={GIG_SUB_LABELS}
                        gigSubKind={gigSubKind}
                        onGigSubKind={setGigSubKind}
                        GIG_TIME_LABELS={GIG_TIME_LABELS}
                        gigTime={gigTime}
                        onGigTime={setGigTime}
                        openNow={openNow}
                        onOpenNow={setOpenNow}
                    />
                </div>

                {/* Sub-filter row moved INTO the Layers menu — kept the JSX shape below for radius panel spacing. */}

                {radiusActive && radiusPanelOpen && (
                    <div data-testid="radius-panel" className="pointer-events-auto glass rounded-2xl shadow-lg px-4 py-3 max-w-sm relative">
                        <button
                            type="button"
                            onClick={() => { clearTimeout(radiusCollapseTimer.current); setRadiusPanelOpen(false); }}
                            data-testid="radius-panel-collapse"
                            aria-label="Collapse radius panel"
                            className="absolute top-2 right-2 w-6 h-6 rounded-full hover:bg-stone-200/60 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
                        >
                            <X size={12} />
                        </button>
                        <div className="flex items-center justify-between text-xs uppercase tracking-[0.2em] font-bold pr-6">
                            <span className="text-muted-foreground">
                                {scoutPin ? "Scouting" : fromMe ? "Within reach of you" : "Within reach of home"}
                            </span>
                            <span className="text-foreground" data-testid="active-radius">{radius} km</span>
                        </div>
                        {scoutPin?.display_name && (
                            <div className="text-xs text-muted-foreground mt-1 truncate" data-testid="scout-address">{scoutPin.display_name}</div>
                        )}
                        <Slider
                            data-testid="home-radius-slider"
                            min={1} max={50} step={1}
                            value={[radius]}
                            onValueChange={([v]) => { setRadius(v); scheduleRadiusCollapse(); }}
                            className="mt-2"
                        />
                        {scoutPin && user && (
                            <button
                                data-testid="save-scout-as-home"
                                onClick={async () => {
                                    try {
                                        const { data } = await api.patch("/auth/me/preferences", {
                                            home_lat: scoutPin.lat, home_lng: scoutPin.lng,
                                            home_address: scoutPin.display_name, filter_radius_km: radius,
                                        });
                                        // update global user via a refresh-triggering event
                                        window.dispatchEvent(new Event("user-updated"));
                                        toast.success("Saved as your home pin");
                                        setScoutPin(null);
                                    } catch (e) { toast.error(apiError(e)); }
                                }}
                                className="mt-2 text-xs font-bold text-primary hover:underline"
                            >
                                Save as home pin
                            </button>
                        )}
                    </div>
                )}
                {radiusActive && !radiusPanelOpen && (
                    <button
                        type="button"
                        onClick={() => setRadiusPanelOpen(true)}
                        data-testid="radius-pill"
                        className="pointer-events-auto glass rounded-full shadow-lg px-3 py-1.5 text-xs font-bold inline-flex items-center gap-1.5 self-start hover:-translate-y-0.5 transition-transform"
                        title="Adjust radius"
                    >
                        <Crosshair size={11} className="text-primary" />
                        {radius} km · <span className="text-muted-foreground font-normal">{scoutPin ? "scouting" : fromMe ? "from you" : "from home"}</span>
                    </button>
                )}
            </div>

            <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
                <SheetTrigger asChild>
                    <button
                        data-testid="open-listings-btn"
                        className={`absolute bottom-6 safe-bottom left-1/2 -translate-x-1/2 z-[500] glass rounded-full px-6 py-3 shadow-lg text-sm font-bold flex items-center gap-2 hover:-translate-y-0.5 transition-transform ${searchFlash ? "results-flash-green" : ""}`}
                    >
                        <MapPin size={16} />
                        Browse <span key={totalCount} className="results-count-pop">{totalCount}</span> results
                    </button>
                </SheetTrigger>
                <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto rounded-t-3xl [&>button:first-child]:w-10 [&>button:first-child]:h-10 [&>button:first-child]:flex [&>button:first-child]:items-center [&>button:first-child]:justify-center [&>button:first-child]:rounded-full [&>button:first-child]:bg-secondary">
                    <VisuallyHidden asChild><SheetTitle>Explore locally</SheetTitle></VisuallyHidden>
                    <VisuallyHidden asChild><SheetDescription>Trending pins, listings and gigs within your radius.</SheetDescription></VisuallyHidden>
                    <h2 className="font-display text-2xl font-bold mb-1">Explore locally</h2>
                    <div className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-4 flex items-center gap-3 flex-wrap">
                        Sorted by {SORT_LABELS[sortBy].toLowerCase()}
                        {q && mapMatches > 0 && (
                            <Button size="sm" onClick={showOnMap} className="h-8 rounded-full normal-case tracking-normal text-xs" data-testid="sheet-show-on-map">
                                <MapPin size={12} className="mr-1" /> Show {mapMatches} on map
                            </Button>
                        )}
                    </div>
                    {trending.length > 0 && (
                        <div className="mb-5" data-testid="trending-strip">
                            <div className="flex items-center gap-2 mb-2">
                                <TrendingUp size={14} className="text-accent" />
                                <div className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Trending near you</div>
                            </div>
                            <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                                {trending.map((it) => {
                                    const kind = it.listing_id ? it.kind : (it.photo_id ? "photo" : "gig");
                                    const meta = KIND_META[kind];
                                    const cover = it.cover_image || it.image_url;
                                    const title = it.name || it.title;
                                    const onClick = () => {
                                        if (it.listing_id) navigate(`/listing/${it.listing_id}`);
                                        else if (it.photo_id) setSelectedPhoto(it);
                                        else if (it.gig_id) setSelectedGig(it);
                                    };
                                    return (
                                        <button key={it.listing_id || it.photo_id || it.gig_id}
                                            data-testid="trending-card"
                                            onClick={onClick}
                                            className="flex-shrink-0 w-40 text-left rounded-2xl overflow-hidden border border-stone-200 bg-card hover:shadow-md hover:-translate-y-0.5 transition-all">
                                            <div className="aspect-[4/3] bg-stone-100">
                                                <CardImage src={cover} alt={title} meta={meta} />
                                            </div>
                                            <div className="p-2.5">
                                                <div className="text-[10px] uppercase tracking-wider font-bold truncate"
                                                    style={{ color: meta?.hex }}>{meta?.label}</div>
                                                <div className="font-display text-sm font-bold truncate mt-0.5">{title}</div>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                    {totalCount === 0 && (
                        <div className="py-16 text-center" data-testid="empty-results">
                            <MapPin className="mx-auto mb-3 text-muted-foreground" size={28} />
                            <div className="font-display text-lg font-bold">Nothing in reach yet</div>
                            <div className="text-sm text-muted-foreground mt-1 max-w-sm mx-auto">
                                {radiusActive
                                    ? "Try widening your radius, clearing the search, or exploring a different spot."
                                    : "Set a home pin or drop an explore pin to see what's near you."}
                            </div>
                            {!radiusActive && (
                                <Button onClick={() => setPinOpen(true)} className="mt-4 rounded-full bg-primary hover:bg-primary/90 hover:text-white">
                                    Set my home pin
                                </Button>
                            )}
                        </div>
                    )}
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        {sortedListings.map((l) => {
                            const meta = KIND_META[resolvePrimaryLayer(l)] || KIND_META.services;
                            const km = origin ? dist(l) : null;
                            const isVerified = (l.rating_count || 0) >= 3;
                            const isNew = l.created_at && (Date.now() - new Date(l.created_at).getTime()) < 7 * 86400000;
                            return (
                                <Link
                                    to={`/listing/${l.listing_id}`}
                                    key={l.listing_id}
                                    data-testid={`listing-card-${l.listing_id}`}
                                    className="group bg-card border border-stone-200 rounded-2xl overflow-hidden hover:shadow-lg transition-shadow"
                                >
                                    <div className="aspect-[4/3] overflow-hidden bg-stone-100 relative">
                                        <CardImage src={l.cover_image} alt={l.name} meta={meta} />
                                        {isNew && (
                                            <span data-testid="new-ribbon"
                                                className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-bold bg-accent text-accent-foreground flex items-center gap-1">
                                                <Sparkles size={9} /> New
                                            </span>
                                        )}
                                        {km !== null && (
                                            <span data-testid="distance-badge" className="absolute bottom-2 right-2 px-2 py-0.5 rounded-full text-[10px] font-bold bg-white/90 text-foreground shadow-sm">
                                                {km < 1 ? `${Math.round(km * 1000)} m` : `${km.toFixed(1)} km`}
                                            </span>
                                        )}
                                    </div>
                                    <div className="p-4">
                                        <div className="text-xs uppercase tracking-[0.2em] font-bold flex items-center gap-1.5" style={{ color: meta?.hex }}>
                                            {meta?.label} • {l.category}
                                            {isVerified && (
                                                <span data-testid="verified-badge" title="3+ reviews"
                                                    className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] bg-green-100 text-green-800 normal-case tracking-normal">
                                                    <ShieldCheck size={8} /> Verified
                                                </span>
                                            )}
                                        </div>
                                        <h3 className="font-display text-lg font-bold mt-1">{l.name}</h3>
                                        <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{l.description}</p>
                                    </div>
                                </Link>
                            );
                        })}
                        {sortedPhotos.map((p) => {
                            const km = origin ? dist(p) : null;
                            return (
                                <button
                                    key={p.photo_id}
                                    data-testid={`photo-card-${p.photo_id}`}
                                    onClick={() => setSelectedPhoto(p)}
                                    className="group text-left bg-card border border-stone-200 rounded-2xl overflow-hidden hover:shadow-lg transition-shadow"
                                >
                                    <div className="aspect-[4/3] overflow-hidden bg-stone-100 relative">
                                        <CardImage src={p.image_url} alt={p.title} meta={KIND_META.photo} />
                                        {km !== null && (
                                            <span data-testid="distance-badge" className="absolute bottom-2 right-2 px-2 py-0.5 rounded-full text-[10px] font-bold bg-white/90 text-foreground shadow-sm">
                                                {km < 1 ? `${Math.round(km * 1000)} m` : `${km.toFixed(1)} km`}
                                            </span>
                                        )}
                                    </div>
                                    <div className="p-4">
                                        <div className="text-xs uppercase tracking-[0.2em] font-bold" style={{ color: KIND_META.photo.hex }}>
                                            Photo spot • {p.location_name}
                                        </div>
                                        <h3 className="font-display text-lg font-bold mt-1">{p.title}</h3>
                                        <p className="text-sm text-muted-foreground mt-1">by {p.photographer_name}</p>
                                    </div>
                                </button>
                            );
                        })}
                        {sortedGigs.map((g) => {
                            const km = origin ? dist(g) : null;
                            const isNew = g.created_at && (Date.now() - new Date(g.created_at).getTime()) < 7 * 86400000;
                            return (
                                <button
                                    key={g.gig_id}
                                    data-testid={`gig-card-${g.gig_id}`}
                                    onClick={() => setSelectedGig(g)}
                                    className="group text-left bg-card border border-stone-200 rounded-2xl overflow-hidden hover:shadow-lg transition-shadow"
                                >
                                    <div className="aspect-[4/3] overflow-hidden bg-stone-100 relative">
                                        <CardImage src={g.cover_image} alt={g.title} meta={KIND_META.gig} />
                                        {isNew && (
                                            <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-bold bg-accent text-accent-foreground flex items-center gap-1">
                                                <Sparkles size={9} /> New
                                            </span>
                                        )}
                                        {km !== null && (
                                            <span className="absolute bottom-2 right-2 px-2 py-0.5 rounded-full text-[10px] font-bold bg-white/90 text-foreground shadow-sm">
                                                {km < 1 ? `${Math.round(km * 1000)} m` : `${km.toFixed(1)} km`}
                                            </span>
                                        )}
                                    </div>
                                    <div className="p-4">
                                        <div className="text-xs uppercase tracking-[0.2em] font-bold" style={{ color: KIND_META.gig.hex }}>
                                            {GIG_SUB_LABELS[g.kind] || "Gig"} • {g.start_at ? new Date(g.start_at).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" }) : ""}
                                        </div>
                                        <h3 className="font-display text-lg font-bold mt-1">{g.title}</h3>
                                        <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{g.description || `by ${g.performer_name}`}</p>
                                    </div>
                                </button>
                            );
                        })}
                    </div>

                    {/* "Further afield" — search matches that are beyond the current radius.
                        Only appears when the user has typed a query, so casual radius browsing
                        doesn't get diluted. Ordered by distance so nearest-out shows first. */}
                    {outOfRadiusCount > 0 && (
                        <div className="mt-8 pt-6 border-t border-stone-200" data-testid="further-afield">
                            <div className="flex items-center gap-2 mb-1">
                                <Navigation size={14} className="text-muted-foreground" />
                                <div className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">
                                    Further afield · {outOfRadiusCount}
                                </div>
                            </div>
                            <p className="text-xs text-muted-foreground mb-4 max-w-xl">
                                Matches for &ldquo;{q}&rdquo; beyond your {radius} km radius. Tap one to see it on the map — or pick a new home pin to make it your local.
                            </p>
                            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                                {outOfRadius.listings.map((l) => {
                                    const meta = KIND_META[resolvePrimaryLayer(l)] || KIND_META.services;
                                    const km = origin ? dist(l) : null;
                                    // Card click flies the map to the pin's coords instead of navigating
                                    // away — matches the user's mental model of "show me on the map",
                                    // and keeps them one tap away from moving their home pin here.
                                    const flyToPin = () => {
                                        setFlyTarget({ lat: l.lat, lng: l.lng, zoom: 14, key: Date.now() });
                                        setSelected(l);
                                        setSheetOpen(false);
                                    };
                                    return (
                                        <button key={l.listing_id}
                                            type="button"
                                            onClick={flyToPin}
                                            data-testid={`far-listing-${l.listing_id}`}
                                            className="group text-left bg-card border border-stone-200 rounded-2xl overflow-hidden hover:shadow-lg transition-shadow opacity-90 hover:opacity-100"
                                        >
                                            <div className="aspect-[4/3] overflow-hidden bg-stone-100 relative">
                                                <CardImage src={l.cover_image} alt={l.name} meta={meta} />
                                                {km !== null && (
                                                    <span className="absolute bottom-2 right-2 px-2 py-0.5 rounded-full text-[10px] font-bold bg-white/90 text-foreground shadow-sm">
                                                        {km < 1 ? `${Math.round(km * 1000)} m` : `${km.toFixed(0)} km away`}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="p-4">
                                                <div className="text-xs uppercase tracking-[0.2em] font-bold" style={{ color: meta?.hex }}>
                                                    {meta?.label} • {l.category}
                                                </div>
                                                <h3 className="font-display text-lg font-bold mt-1">{l.name}</h3>
                                                <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{l.address || l.description}</p>
                                            </div>
                                        </button>
                                    );
                                })}
                                {outOfRadius.photos.map((p) => {
                                    const km = origin ? dist(p) : null;
                                    const flyToPhoto = () => {
                                        setFlyTarget({ lat: p.lat, lng: p.lng, zoom: 14, key: Date.now() });
                                        setSelectedPhoto(p);
                                        setSheetOpen(false);
                                    };
                                    return (
                                        <button key={p.photo_id}
                                            data-testid={`far-photo-${p.photo_id}`}
                                            onClick={flyToPhoto}
                                            className="group text-left bg-card border border-stone-200 rounded-2xl overflow-hidden hover:shadow-lg transition-shadow opacity-90 hover:opacity-100"
                                        >
                                            <div className="aspect-[4/3] overflow-hidden bg-stone-100 relative">
                                                <CardImage src={p.image_url} alt={p.title} meta={KIND_META.photo} />
                                                {km !== null && (
                                                    <span className="absolute bottom-2 right-2 px-2 py-0.5 rounded-full text-[10px] font-bold bg-white/90 text-foreground shadow-sm">
                                                        {km < 1 ? `${Math.round(km * 1000)} m` : `${km.toFixed(0)} km away`}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="p-4">
                                                <div className="text-xs uppercase tracking-[0.2em] font-bold" style={{ color: KIND_META.photo.hex }}>
                                                    Photo spot • {p.location_name}
                                                </div>
                                                <h3 className="font-display text-lg font-bold mt-1">{p.title}</h3>
                                                <p className="text-sm text-muted-foreground mt-1">by {p.photographer_name}</p>
                                            </div>
                                        </button>
                                    );
                                })}
                                {outOfRadius.gigs.map((g) => {
                                    const km = origin ? dist(g) : null;
                                    const flyToGig = () => {
                                        setFlyTarget({ lat: g.lat, lng: g.lng, zoom: 14, key: Date.now() });
                                        setSelectedGig(g);
                                        setSheetOpen(false);
                                    };
                                    return (
                                        <button key={g.gig_id}
                                            data-testid={`far-gig-${g.gig_id}`}
                                            onClick={flyToGig}
                                            className="group text-left bg-card border border-stone-200 rounded-2xl overflow-hidden hover:shadow-lg transition-shadow opacity-90 hover:opacity-100"
                                        >
                                            <div className="aspect-[4/3] overflow-hidden bg-stone-100 relative">
                                                <CardImage src={g.cover_image} alt={g.title} meta={KIND_META.gig} />
                                                {km !== null && (
                                                    <span className="absolute bottom-2 right-2 px-2 py-0.5 rounded-full text-[10px] font-bold bg-white/90 text-foreground shadow-sm">
                                                        {km < 1 ? `${Math.round(km * 1000)} m` : `${km.toFixed(0)} km away`}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="p-4">
                                                <div className="text-xs uppercase tracking-[0.2em] font-bold" style={{ color: KIND_META.gig.hex }}>
                                                    {GIG_SUB_LABELS[g.kind] || "Gig"} • {g.start_at ? new Date(g.start_at).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" }) : ""}
                                                </div>
                                                <h3 className="font-display text-lg font-bold mt-1">{g.title}</h3>
                                                <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{g.description || `by ${g.performer_name}`}</p>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </SheetContent>
            </Sheet>

            {selected && (
                <div className="absolute bottom-24 left-4 right-4 sm:left-auto sm:right-4 sm:w-96 z-[500] fade-up">
                    <div className="glass max-sm:!bg-card rounded-2xl shadow-xl overflow-hidden relative max-h-[60vh] overflow-y-auto">
                        {/* Always-visible close button — sits on the top-right corner of the whole card
                            so it works with OR without a cover image, and on both light + dark headers. */}
                        <button onClick={() => setSelected(null)} data-testid="close-selected"
                            aria-label="Close"
                            className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/95 shadow-md flex items-center justify-center hover:bg-white hover:-translate-y-0.5 transition-all z-10 ring-1 ring-stone-200">
                            <X size={16} />
                        </button>
                        {selected.cover_image && (
                            <div className="relative aspect-[16/10]">
                                <img loading="lazy" decoding="async" src={thumb(selected.cover_image, 600)} alt={selected.name} className="w-full h-full object-cover" />
                            </div>
                        )}
                        <div className="p-4">
                            <div className="text-xs uppercase tracking-[0.2em] font-bold" style={{ color: KIND_META[resolvePrimaryLayer(selected)]?.hex }}>
                                {selected.category}
                            </div>
                            <h3 className="font-display text-xl font-bold mt-1">{selected.name}</h3>
                            {selected.founding && <FoundingBadge className="mt-1.5" />}
                            {selected.is_fuzzy && (
                                <p data-testid="home-fuzzy-note" className="text-xs text-muted-foreground mt-1.5">
                                    ≈ Approximate area{selected.address ? ` — around ${selected.address}` : ""}. Exact address shared on booking or request.
                                </p>
                            )}
                            <p className="text-sm text-muted-foreground mt-1 line-clamp-3">{selected.description}</p>
                            <div className="text-xs text-muted-foreground mt-2 flex items-center gap-1 flex-wrap">
                                <MapPin size={12} /> {selected.address}
                                {selected.is_fuzzy && (
                                    <span data-testid="home-fuzzy-badge" className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-bold bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded-full ml-1">
                                        Approximate
                                    </span>
                                )}
                                {selected.affiliation_count > 0 && (
                                    <span data-testid="home-aff-count" className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-bold bg-primary/10 text-primary border border-primary/20 px-1.5 py-0.5 rounded-full ml-1">
                                        {selected.affiliation_count} linked
                                    </span>
                                )}
                            </div>
                            <div className="mt-4 flex gap-2">
                                <Button data-testid="view-listing-btn" onClick={() => navigate(`/listing/${selected.listing_id}`)}
                                    className="flex-1 rounded-full bg-primary hover:bg-primary/90 hover:text-white">
                                    Visit shop
                                </Button>
                                <FollowPinButton listing={selected} variant="card" />
                                {!selected.is_fuzzy && (selected.lat || selected.lng) && (
                                    <a
                                        href={`https://www.google.com/maps/dir/?api=1&destination=${selected.lat},${selected.lng}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        data-testid="map-directions-btn"
                                        title={`Directions to ${selected.name}`}
                                        className="inline-flex items-center gap-1.5 text-sm font-bold rounded-full bg-ink text-white px-4 hover:-translate-y-0.5 transition-transform"
                                    >
                                        <Navigation size={14} /> Directions
                                    </a>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Photo lightbox */}
            <PhotoLightbox photo={selectedPhoto} open={!!selectedPhoto} onOpenChange={(v) => !v && setSelectedPhoto(null)} />
            <GigLightbox gig={selectedGig} open={!!selectedGig} onOpenChange={(v) => !v && setSelectedGig(null)} />
            <HomePinDialog open={pinOpen} onOpenChange={setPinOpen} />
            <ScoutTripsDialog
                open={tripsOpen}
                onOpenChange={setTripsOpen}
                currentPin={scoutPin}
                currentRadius={radius}
                onOpen={(trip) => {
                    setScoutPin({ lat: trip.lat, lng: trip.lng, display_name: trip.address });
                    setRadius(trip.radius_km);
                    setFromMe(false);
                }}
                onSaved={() => { /* trip added */ }}
            />
            <ExploreLocationDialog
                open={exploreLocOpen}
                onOpenChange={setExploreLocOpen}
                onPick={handleExploreLocationPick}
            />
            <OnboardingTour />
            <Dialog open={geoPrePromptOpen} onOpenChange={setGeoPrePromptOpen}>
                <DialogContent className="max-w-sm">
                    <DialogHeader>
                        <DialogTitle className="font-display flex items-center gap-2">
                            <Compass size={18} className="text-primary" /> Show me what's near me?
                        </DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-muted-foreground">
                        From Here uses your location to show makers, gigs and photos nearby.
                        We don't store it — your browser sends it to the map only while you're using this feature.
                    </p>
                    <p className="text-xs text-muted-foreground">
                        You can change your mind anytime. See our <Link to="/privacy" className="underline hover:text-primary">Privacy Policy</Link>.
                    </p>
                    <DialogFooter className="gap-2 sm:gap-2">
                        <Button variant="outline" onClick={() => setGeoPrePromptOpen(false)} className="rounded-full"
                            data-testid="geo-prompt-cancel">
                            Not now
                        </Button>
                        <Button onClick={confirmGeoPrompt} className="rounded-full bg-primary hover:bg-primary/90 hover:text-white"
                            data-testid="geo-prompt-allow">
                            Use my location
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

const MAP_CACHE = "fh_map_cache_v1";
function readMapCache() {
    try {
        const c = JSON.parse(localStorage.getItem(MAP_CACHE));
        return c && Array.isArray(c.listings) ? c : null;
    } catch { return null; }
}
function writeMapCache(c) {
    try { localStorage.setItem(MAP_CACHE, JSON.stringify(c)); } catch { /* quota — skip caching */ }
}

function MapFreshness({ refreshing, cachedAt }) {
    if (refreshing) {
        return (
            <div data-testid="map-updating" className="absolute top-3 left-1/2 -translate-x-1/2 z-[500] flex items-center gap-1.5 px-3 py-1 rounded-full bg-card/90 backdrop-blur border shadow-sm text-xs font-bold">
                <span className="w-2 h-2 rounded-full bg-primary animate-pulse" /> Updating…
            </div>
        );
    }
    if (!cachedAt) return null;
    const t = new Date(cachedAt).toLocaleString(undefined, { weekday: "short", hour: "2-digit", minute: "2-digit" });
    return (
        <div data-testid="map-offline" className="absolute top-3 left-1/2 -translate-x-1/2 z-[500] px-3 py-1 rounded-full bg-amber-100 border border-amber-300 text-amber-900 shadow-sm text-xs font-bold whitespace-nowrap">
            No signal — showing pins from {t}
        </div>
    );
}
